/* ============================================================
   storage — the app shell.

   Holds the session, the drive list and which view is showing;
   everything else is a child that receives what it needs. The
   drive list is polled rather than fetched once: a mount can
   drop while the page is open, and a file view that still
   believes in a dead drive is worse than one that says so.

   The page is served from the same bundle as the teg dashboard,
   so it brands itself at runtime — title, description, icons.

   Where you are lives in the URL and not in this component's
   head: the folder, the selection, the file open in the preview
   and the second the player is on (urlstate.js writes the
   scheme). So a refresh lands where you were, and the address
   bar is a link worth sending. This file owns the one rule that
   makes Back mean something: pushState for real navigation —
   entering a folder, opening a file, leaving the viewer — and
   replaceState for everything that changes while you sit still.
   ============================================================ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './storage.css';
import { Icon, describeSkips, fmtBytes, isNoopMove, parentOf, fileUrl } from './lib';
import { HOME, buildUrl, parseUrl } from './urlstate';
import { api, setExpiryHandler } from './api';
import { useTransfers } from './transfers';
import { useDuplicateCheck } from './Duplicates';
import Login from './Login';
import Tree from './Tree';
import Browser from './Browser';
import Dashboard from './Dashboard';
import AllFiles from './AllFiles';
import { PublicShare } from './Share';
import TransferPanel from './TransferPanel';
import Preview from './Preview';
import Viewer from './Viewer';
import AdminUsers from './AdminUsers';
import Trash from './Trash';
import Home, { rememberOpened } from './Home';
import { useTrimJobs } from './Trim';
import { reachable } from './direct';

const SESSION_KEY = 'ojee.storage.session';
// The right-hand pane's width, dragged by the divider between it and the
// file list. Bounded so neither side can be dragged away to nothing: the
// maximum leaves the list at least a readable column.
const RAIL_KEY = 'ojee.storage.railw';
const RAIL_DEFAULT = 360;
const RAIL_MIN = 300;
const RAIL_MAX = 1400;
const railMax = () => Math.max(RAIL_MIN, Math.min(RAIL_MAX, window.innerWidth - 520));
const DRIVE_POLL_MS = 30000;
// How often a playing file is allowed to rewrite the address bar. Every
// timeupdate would be four writes a second for no gain: a refresh that lands
// within a second or two of where you were has kept your place.
const POS_URL_MS = 2000;

/* A toast may carry one action — "Undo" after a delete — and then stays
   long enough to be used. */
function useToasts(external) {
  const [list, setList] = useState([]);
  const push = useCallback((kind, title, body, action) => {
    if (external) { external(kind, title, body, action); return; }
    const id = Math.random().toString(36).slice(2);
    setList((l) => [...l, { id, kind, title, body, action }]);
    setTimeout(() => setList((l) => l.filter((t) => t.id !== id)), action ? 12000 : 6000);
  }, [external]);
  const node = external ? null : (
    <div className="toast-stack st-toasts">
      {list.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind === 'err' ? 'err' : t.kind === 'warn' ? 'warn' : t.kind === 'ok' ? 'ok' : 'info'}`}>
          <div className="toast-txt">
            <strong>{t.title}</strong>
            {t.body && <div className="meta">{t.body}</div>}
          </div>
          {t.action && (
            <button type="button" className="btn btn--sm st-toast-act"
                    onClick={() => { setList((l) => l.filter((x) => x.id !== t.id)); t.action.run(); }}>
              {t.action.label}
            </button>
          )}
          <button type="button" className="toast-x" aria-label="Dismiss"
                  onClick={() => setList((l) => l.filter((x) => x.id !== t.id))}>
            <Icon name="close" size={12}/>
          </button>
        </div>
      ))}
    </div>
  );
  return [push, node];
}

/** Brand the page for storage; the bundle also serves the teg dashboard. */
function useBranding(isPublic) {
  useEffect(() => {
    // A child's effects run before its parent's, so the public page setting
    // its own title here would be overwritten a moment later by this one.
    document.title = isPublic ? 'storage · shared' : 'storage · files';
    const set = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.startsWith('meta') ? 'meta' : 'link');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    set('link[rel="icon"]', { rel: 'icon', type: 'image/svg+xml', href: '/storage-icon.svg' });
    set('link[rel="apple-touch-icon"]', { rel: 'apple-touch-icon', href: '/storage-apple-touch-icon.png' });
    set('meta[name="description"]', { name: 'description', content: 'Network storage across several drives' });
    set('meta[name="apple-mobile-web-app-title"]', { name: 'apple-mobile-web-app-title', content: 'storage' });
  }, [isPublic]);
}

export default function StorageApp({ toast: externalToast }) {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
  });
  // The link that opened this tab, read once and before anything else: every
  // piece of state below starts from it.
  const boot = useRef(parseUrl()).current;
  const [expiredNote, setExpiredNote] = useState('');
  const [drives, setDrives] = useState([]);
  const [quota, setQuota] = useState(null);
  const [recent, setRecent] = useState(null);   // null until the first answer: the dashboard shows a skeleton
  const [view, setView] = useState(boot.view);
  const [drive, setDrive] = useState(boot.drive);
  const [path, setPath] = useState(boot.path);
  // Selection and filter live here rather than in the file list, because the
  // URL has to be able to state them and the list is not the only thing that
  // changes them.
  const [selection, setSelection] = useState(boot.selection);
  const [query, setQuery] = useState(boot.query);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showTransfers, setShowTransfers] = useState(false);
  const [showTree, setShowTree] = useState(false);
  // /s/<token> — read once, before anything asks who you are.
  const [publicToken] = useState(() => {
    const m = typeof window !== 'undefined'
      && window.location.pathname.match(/\/s\/([A-Za-z0-9_-]{20,64})\/?$/);
    return m ? m[1] : null;
  });
  // `fileId` is what the URL says is open; `preview` is the row behind it
  // once it has been fetched, plus the files it sits among so the arrows can
  // step through the folder. That list is a snapshot on purpose: the folder
  // may change while the pane is open, and jumping to a different file
  // because something was uploaded elsewhere would be worse than a slightly
  // stale neighbour.
  const [fileId, setFileId] = useState(boot.file);
  const [preview, setPreview] = useState(null);
  // `viewer=1` — the player in a tab of its own, with the rest of the app
  // taken away. Same params as the pane; this one flag is the difference.
  const [viewerTab, setViewerTab] = useState(boot.viewer && !!boot.file);
  // The second a link asked for, which beats any remembered position.
  const [startAt, setStartAt] = useState(boot.at);
  // How wide the right-hand pane is, dragged by its divider. Remembered
  // because the width someone chose for watching is the width they want the
  // next time too.
  const [railW, setRailW] = useState(() => {
    const saved = Number(localStorage.getItem(RAIL_KEY));
    return Number.isFinite(saved) && saved >= RAIL_MIN ? Math.min(saved, RAIL_MAX) : RAIL_DEFAULT;
  });
  // Cut/copy survives navigation — that is the whole point of a clipboard.
  const [clipboard, setClipboard] = useState(null);
  const [drivesLoading, setDrivesLoading] = useState(true);
  const [toast, toastNode] = useToasts(externalToast);
  const seenTransfers = useRef(0);
  const headRef = useRef(null);
  // The live playback position, in whole seconds. A ref and not state: it
  // moves four times a second and nothing on screen reads it from here.
  const atRef = useRef(boot.at);
  const wroteAt = useRef(0);        // when the URL last took a position
  const pushNext = useRef(false);   // the next URL write is a navigation
  const urlRef = useRef(null);      // the state the URL is built from
  const bootChecked = useRef(false);
  const folderFiles = useRef([]);   // the folder's files, as the list has them
  const fileIdRef = useRef(boot.file);
  useBranding(!!publicToken);

  useEffect(() => {
    const el = headRef.current;
    if (!el) return undefined;
    const publish = () => document.documentElement.style.setProperty('--st-head-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const token = session?.token;

  // Assigned during the render rather than in an effect: a child's cleanup
  // runs before this component's effects do, and it has to see the file that
  // is open now.
  fileIdRef.current = fileId;

  /* ── the URL ───────────────────────────────────────────────────────────
     One writer. Everything that can be said in a link is gathered here and
     written out whenever it changes; `pushNext` marks the changes that are
     navigation, and everything else replaces the entry it is already on.
     A write that would not change the address is not a write at all, which
     is what keeps Back and Forward from being rewritten under them. */
  const writeUrl = useCallback((push) => {
    if (!urlRef.current) return;
    const next = buildUrl(urlRef.current);
    const here = `${window.location.pathname}${window.location.search}`;
    if (next === here) return;
    if (push) window.history.pushState({}, '', next);
    else window.history.replaceState(window.history.state, '', next);
  }, []);

  useEffect(() => {
    urlRef.current = {
      view, drive, path, query, selection, file: fileId, at: atRef.current, viewer: viewerTab,
    };
    if (publicToken || !token) return;   // a share link and the sign-in form own their own address
    writeUrl(pushNext.current);
    pushNext.current = false;
  }, [view, drive, path, query, selection, fileId, viewerTab, publicToken, token, writeUrl]);

  /** Where the player is. Throttled, because this arrives several times a
   *  second; `force` is pause, seek, the end of a file and leaving the page,
   *  where the exact second is the whole point. */
  const reportAt = useCallback((seconds, force, key) => {
    // A player reports one last time as it unmounts, which is after the page
    // has moved on to the next file. That second belongs to the file it was
    // measured in, not to this one.
    if (key && key !== fileIdRef.current) return;
    const s = Math.max(0, Math.floor(seconds || 0));
    const same = s === atRef.current;
    atRef.current = s;
    if (urlRef.current) urlRef.current.at = s;
    if (same && !force) return;
    const now = Date.now();
    if (!force && now - wroteAt.current < POS_URL_MS) return;
    wroteAt.current = now;
    writeUrl(false);
  }, [writeUrl]);

  /* Back and Forward. The address is the truth; this reads it back in.
     Nothing here has to suppress the writer above: the state it produces
     builds the address it came from, and an identical address is not
     written. */
  useEffect(() => {
    const onPop = () => {
      const s = parseUrl();
      atRef.current = s.at;
      setStartAt(s.at);
      setView(s.view); setDrive(s.drive); setPath(s.path);
      setQuery(s.query); setSelection(s.selection);
      setViewerTab(s.viewer && !!s.file);
      setFileId(s.file);
      // The pane stays only if it is showing the file the address names;
      // anything else is re-fetched by the effect that resolves `file`.
      setPreview((p) => (p && s.file === p.file.id ? p : null));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    localStorage.setItem(RAIL_KEY, String(railW));
  }, [railW]);

  // For Home's "opened lately": what this browser opened, nothing more.
  useEffect(() => { if (preview?.file) rememberOpened(preview.file); }, [preview?.file]);

  // Dragging the divider. Pointer capture keeps the drag alive over the
  // video underneath, which would otherwise swallow the move events.
  const dragRail = useCallback((e) => {
    e.preventDefault();
    // Capture keeps the drag alive over the video; not every pointer can be
    // captured, and failing to is not a reason to abandon the drag.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    const move = (ev) => {
      const w = window.innerWidth - ev.clientX;
      setRailW(Math.max(RAIL_MIN, Math.min(railMax(), Math.round(w))));
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.body.classList.remove('st-dragging');
    };
    document.body.classList.add('st-dragging');
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }, []);
  const nudgeRail = useCallback((e) => {
    const stepBy = e.shiftKey ? 96 : 24;
    if (e.key === 'ArrowLeft') { e.preventDefault(); setRailW((w) => Math.min(railMax(), w + stepBy)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setRailW((w) => Math.max(RAIL_MIN, w - stepBy)); }
    else if (e.key === 'Home') { e.preventDefault(); setRailW(railMax()); }
    else if (e.key === 'End') { e.preventDefault(); setRailW(RAIL_MIN); }
  }, []);

  // The login response carries a username and role but not the id, and "is
  // this mine?" is an id comparison on every row. Fetch it once per session.
  useEffect(() => {
    if (!token || session.id) return;
    let live = true;
    api.me(token)
      .then((who) => { if (live) setSession((s) => ({ ...s, id: who.id })); })
      .catch(() => { /* an expired token is handled by the expiry hook */ });
    return () => { live = false; };
  }, [token, session?.id]);

  useEffect(() => {
    setExpiryHandler(() => {
      setSession(null);
      localStorage.removeItem(SESSION_KEY);
      setExpiredNote('Your session ended. Sign in to carry on — anything half-uploaded resumes.');
    });
  }, []);

  const signIn = (s) => {
    setSession(s);
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    setExpiredNote('');
  };
  const signOut = () => {
    setSession(null);
    localStorage.removeItem(SESSION_KEY);
  };

  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  /* The direct route (direct.js): a drive whose agent this device can reach
     takes uploads and serves downloads itself, over the local network. The
     server says where it is (drive.direct, only for people offered it); this
     asks whether it answers from here. */
  const drivesRef = useRef([]);
  drivesRef.current = drives;
  const directFor = useCallback(async (driveId) => {
    const base = drivesRef.current.find((d) => d.id === driveId)?.direct;
    return base && (await reachable(base)) ? base : null;
  }, []);
  const directBaseOf = useCallback((driveId) => drivesRef.current.find((d) => d.id === driveId)?.direct || null, []);
  useEffect(() => { drives.forEach((d) => { if (d.direct) reachable(d.direct); }); }, [drives]);

  const transfers = useTransfers(token, {
    directFor,
    onUploaded: () => onChanged(),
    onError: (e) => toast('err', 'Transfer problem', e.message),
  });

  // Every upload goes through the duplicate check first: files already stored
  // byte for byte are listed before anything is sent.
  const startUpload = useCallback((entries, target) => {
    const n = transfers.upload(entries, target);
    if (n) setShowTransfers(true);
    return n;
  }, [transfers]);
  const [checkUpload, dupDialog] = useDuplicateCheck(token, startUpload, { toast, onChanged, drives });

  /* Trims run on the server and outlive the player that started them, so
     they are watched from here: the transfers panel lists them, a toast says
     when one lands, and a video trimmed in place is re-read wherever it is
     showing — same id, new bytes. */
  const [viewerReload, setViewerReload] = useState(0);
  const trim = useTrimJobs(token, {
    toast,
    drives,
    onChanged,
    onReplaced: (id) => {
      setViewerReload((n) => n + 1);
      api.file(token, id).then((f) => {
        setPreview((p) => (p && p.file.id === id
          ? { ...p, file: f, list: p.list.map((x) => (x.id === id ? f : x)) } : p));
      }).catch(() => {});
    },
    onShowFile: (j) => {
      pushNext.current = true;
      atRef.current = 0;
      setViewerTab(false);
      setDrive(j.commit_drive); setPath(j.commit_path || '/'); setView('files');
      setPreview(null); setFileId(null);
      setSelection(new Set([`f:${j.commit_file}`]));
      setQuery('');
    },
    onOpenFile: (j) => {
      pushNext.current = true;
      atRef.current = 0;
      setStartAt(0);
      setPreview((p) => (p && p.file.id === j.file_id ? p : null));
      setFileId(j.file_id);
    },
  });

  /* drives + quota, on a poll so a dropped mount shows up */
  const loadDrives = useCallback(async () => {
    if (!token) return;
    try {
      const [d, q] = await Promise.all([api.drives(token), api.quota(token)]);
      setDrives(d); setQuota(q);
      const cur = urlRef.current?.drive || HOME.drive;
      if (d.length && !d.some((x) => x.id === cur)) {
        // A link naming a drive this server does not have. Landing on the
        // first one is better than an empty list that explains nothing.
        const next = d[0].id;
        setDrive(next);
        setPath('/');
        toast('warn', 'That drive is not here', `Showing ${d[0].label || next} instead.`);
      }
    } catch { /* the view shows its own error */ }
    finally { setDrivesLoading(false); }
  }, [token, toast]);

  useEffect(() => {
    if (!token) return undefined;
    loadDrives();
    const t = setInterval(loadDrives, DRIVE_POLL_MS);
    return () => clearInterval(t);
  }, [token, loadDrives, refreshKey]);

  /* recent files for the dashboard */
  useEffect(() => {
    if (!token || view !== 'dashboard' || !drives.length) return;
    let live = true;
    Promise.all(drives.filter((d) => d.online).map((d) => api.allFiles(token, d.id).catch(() => [])))
      .then((lists) => {
        if (!live) return;
        setRecent(lists.flat().sort((a, b) => b.created_at - a.created_at).slice(0, 10));
      });
    return () => { live = false; };
  }, [token, view, drives, refreshKey]);

  /* a finished transfer nudges the panel open the first time */
  useEffect(() => {
    if (transfers.items.length > seenTransfers.current && !showTransfers) {
      seenTransfers.current = transfers.items.length;
      setShowTransfers(true);
    }
    seenTransfers.current = transfers.items.length;
  }, [transfers.items.length, showTransfers]);

  /* ── opening things ────────────────────────────────────────────────────
     Each of these is navigation, so each pushes: Back closes the file you
     opened, or returns you to the folder you came from, rather than walking
     backwards through a hundred playback positions. */
  const openPreview = useCallback((file, siblings) => {
    pushNext.current = true;
    atRef.current = 0;
    setStartAt(0);
    setPreview({ file, list: siblings?.length ? siblings : [file] });
    setFileId(file.id);
  }, []);

  const closePreview = useCallback(() => {
    pushNext.current = true;
    atRef.current = 0;
    setPreview(null);
    setFileId(null);
  }, []);

  const stepPreview = useCallback((delta) => {
    if (!preview) return;
    const at = preview.list.findIndex((f) => f.id === preview.file.id);
    const next = preview.list[at + delta];
    if (!next) return;
    pushNext.current = true;
    atRef.current = 0;
    setStartAt(0);
    setPreview({ ...preview, file: next, stub: false });
    setFileId(next.id);
  }, [preview]);

  const navigate = useCallback((d, p, select) => {
    pushNext.current = true;
    atRef.current = 0;
    setDrive(d); setPath(p); setView('files'); setShowTree(false);
    setPreview(null); setFileId(null);
    // A folder you have just entered has nothing selected and nothing typed
    // into the filter; both belong to the folder you left.
    setSelection(select ? new Set(select) : new Set());
    setQuery('');
  }, []);

  const goView = useCallback((v) => {
    pushNext.current = true;
    setView(v);
  }, []);

  /* Search is every file you can see (AllFiles), opened from the box in the
     header, from Home, or from an old ?view=all link. `searchInit` is how it
     should start sorted — Home's "see all" wants newest first. */
  const [searchInit, setSearchInit] = useState({ sort: 'added', owner: '', n: 0 });
  const goSearch = useCallback((q, sort = 'added', owner = '') => {
    pushNext.current = true;
    setSearchInit((s) => ({ sort, owner, n: s.n + 1 }));
    setQuery(q || '');
    setSelection(new Set());
    setView('search');
  }, []);

  /* Open one file where it lives, from anywhere (Home, search). The row is
     fetched by the effect that resolves `fileId`, which also moves the list
     to its folder. */
  const openFile = useCallback((file, at = 0) => {
    pushNext.current = true;
    atRef.current = at || 0;
    setStartAt(at || 0);
    setView('files');
    if (file.drive) setDrive(file.drive);
    if (file.path) setPath(file.path);
    setSelection(new Set());
    setQuery('');
    setPreview(null);
    setFileId(file.id);
  }, []);

  /* ── a link that names a file ──────────────────────────────────────────
     `?file=<id>` arrives knowing an id and nothing else, so the row has to
     be fetched before the pane can show anything — and the folder it lives
     in follows from the row, not from the link, in case it has moved since
     the link was written. */
  useEffect(() => {
    if (!token || !fileId || viewerTab) return undefined;
    if (preview && preview.file.id === fileId) return undefined;
    let live = true;
    api.file(token, fileId)
      .then((f) => {
        if (!live) return;
        // The folder's file rows may already have arrived, in which case the
        // arrows work from the first frame; otherwise the pane opens on its
        // own and adopts them when the list lands.
        const known = folderFiles.current.some((x) => x.id === f.id);
        setPreview({ file: f, list: known ? folderFiles.current : [f], stub: !known });
        if (f.drive && f.path && (f.drive !== drive || f.path !== path)) {
          setDrive(f.drive); setPath(f.path); setView('files');
        }
      })
      .catch((e) => {
        if (!live) return;
        const locked = e.status === 403;
        toast('warn', locked ? 'That file is locked' : 'That file is gone',
          locked
            ? 'It is inside a secure folder that is not yours to open.'
            : 'The link points at something that is not here any more.');
        atRef.current = 0;
        setStartAt(0);
        setFileId(null);
        setPreview(null);
      });
    return () => { live = false; };
  }, [token, fileId, viewerTab, preview, drive, path, toast]);

  /* A link's folder may have been renamed or deleted since it was written.
     An unknown path lists as empty, which reads as "you emptied this" — so
     it is checked once, against the parent, and only for a link that named
     one. A secure parent throws instead, and the file view says so itself. */
  useEffect(() => {
    if (!token || bootChecked.current) return undefined;
    bootChecked.current = true;
    if (boot.path === '/') return undefined;
    let live = true;
    api.folders(token, boot.drive, parentOf(boot.path))
      .then((rows) => {
        if (!live || urlRef.current?.path !== boot.path || urlRef.current?.drive !== boot.drive) return;
        if (!rows.some((r) => r.path === boot.path)) {
          toast('warn', 'That folder is not there any more',
            'It was renamed, moved or deleted. This is the top of the drive.');
          setPath('/');
          setSelection(new Set());
        }
      })
      .catch(() => { /* secure or offline: the file view has its own words for both */ });
    return () => { live = false; };
  }, [token, boot.path, boot.drive, toast]);

  /* A link that named more files than a link can carry, opened somewhere
     that never held the rest of them. */
  useEffect(() => {
    if (!boot.lostSelection) return;
    toast('info', 'The selection did not come with the link',
      `It named ${boot.lostSelection} items, which only stay selected in the tab they were selected in.`);
  }, [boot.lostSelection, toast]);

  /* The list under the pane, once it has loaded, so a file opened straight
     from a link can still step to its neighbours. Only for a pane that was
     opened that way: one opened by clicking already has the folder it was
     clicked in, and that snapshot is deliberate. */
  const adoptSiblings = useCallback((fileRows) => {
    folderFiles.current = fileRows;
    setPreview((p) => {
      if (!p?.stub || !fileRows.some((f) => f.id === p.file.id)) return p;
      return { ...p, list: fileRows, stub: false };
    });
  }, []);

  /* Rows dragged onto the trash in the sidebar: no question asked, because
     the toast can take it back. */
  const dropOnTrash = useCallback(async (e) => {
    e.preventDefault(); e.stopPropagation();
    const raw = e.dataTransfer.getData('application/x-storage-rows');
    if (!raw) return;
    const moving = JSON.parse(raw);
    try {
      const res = await api.batchDelete(token, moving.files || [], moving.folders || []);
      const n = (res.files || 0) + (res.folders || 0);
      if (n && res.batch) {
        toast('ok', 'Moved to the trash', `${n} item${n === 1 ? '' : 's'}`, {
          label: 'Undo',
          run: async () => {
            try { await api.trashRestore(token, { batch: res.batch }); } catch (ex) { toast('err', 'Could not undo', ex.message); }
            onChanged();
          },
        });
      }
      if (res.skipped?.length) toast('warn', 'Some things were kept', describeSkips(res.skipped));
      setSelection(new Set());
      onChanged();
    } catch (ex) { toast('err', 'Could not delete', ex.message); }
  }, [token, toast, onChanged]);

  const driveRow = drives.find((d) => d.id === drive);
  const activeCount = transfers.active.length + trim.active;
  const totals = useMemo(() => ({
    used: drives.reduce((n, d) => n + d.used, 0),
    // A drive that is not set up yet has no space to count.
    cap: drives.filter((d) => !d.setup_needed).reduce((n, d) => n + d.cap, 0),
  }), [drives]);

  // A public link is not part of the app: no nav, no login, no hint that
  // there is a larger system behind it. Checked before the session gate,
  // because a visitor with a link has no account and must never be shown a
  // sign-in form for something they were given.
  if (publicToken) return <PublicShare token={publicToken}/>;

  if (!token) return (<><Login onLogin={signIn} note={expiredNote}/>{toastNode}</>);

  // The viewer in its own tab: the player, the file's facts and a way back
  // into the folder it lives in. Nothing else — this tab exists to watch one
  // thing. Leaving it is navigation like any other, so it pushes: a reload
  // lands on the browser and Back returns to what was playing.
  if (viewerTab && fileId) {
    const leave = (file) => {
      pushNext.current = true;
      atRef.current = 0;
      setViewerTab(false);
      setFileId(null);
      setStartAt(0);
      if (file) {
        setDrive(file.drive || HOME.drive);
        setPath(file.path || '/');
        setView('files');
        setSelection(new Set([`f:${file.id}`]));
      }
    };
    return (
      <>
        <Viewer
          token={token} fileId={fileId} startAt={startAt} me={session}
          onDownload={(f) => transfers.download(f)}
          onPosition={reportAt}
          onMissing={(locked) => toast('warn', locked ? 'That file is locked' : 'That file is gone',
            locked
              ? 'It is inside a secure folder that is not yours to open.'
              : 'The link points at something that is not here any more.')}
          onShowInFolder={leave}
          onExit={() => leave(null)}
          trim={trim}
          reloadKey={viewerReload}
          directFor={directBaseOf}
        />
        {trim.dialog}
        {toastNode}
      </>
    );
  }

  return (
    <div className="st-app">
      <header className="st-head" ref={headRef}>
        <button type="button" className="iconbtn st-navtoggle" aria-label="Folders" onClick={() => setShowTree((v) => !v)}>
          <Icon name="menu" size={18}/>
        </button>
        <span className="st-brand">storage<span className="st-dot">.</span></span>

        <nav className="st-views" aria-label="Views">
          {[['home', 'Home'], ['files', 'Files'], ['dashboard', 'Drives'], ...(session.role === 'admin' ? [['admin', 'Accounts']] : [])]
            .map(([id, label]) => (
              <button key={id} type="button" className={`st-tab ${view === id ? 'is-on' : ''}`}
                      aria-current={view === id ? 'page' : undefined} onClick={() => goView(id)}>{label}</button>
            ))}
        </nav>

        <form className="st-head-search" role="search"
              onSubmit={(e) => { e.preventDefault(); goSearch(new FormData(e.currentTarget).get('q') || ''); }}>
          <Icon name="search" size={14}/>
          <input name="q" type="search" placeholder="Search files" aria-label="Search every file"
                 key={view === 'search' ? 'in-search' : 'out'} defaultValue={view === 'search' ? query : ''}/>
        </form>
        <div className="st-head-right">
          <button type="button" className={`iconbtn st-head-searchbtn ${view === 'search' ? 'is-live' : ''}`}
                  aria-label="Search every file" onClick={() => goSearch(view === 'search' ? query : '')}>
            <Icon name="search" size={16}/>
          </button>
          <span className="st-total meta">{fmtBytes(totals.used)} of {fmtBytes(totals.cap)} used</span>
          <button type="button" className={`iconbtn st-tr-toggle ${activeCount ? 'is-live' : ''}`}
                  onClick={() => setShowTransfers((v) => !v)}
                  aria-pressed={showTransfers}
                  title={activeCount ? `${activeCount} transfer(s) running` : 'Transfers'}
                  aria-label={activeCount ? `Transfers, ${activeCount} running` : 'Transfers'}>
            <Icon name={activeCount ? 'upload' : 'activity'} size={16}/>
            {activeCount > 0 && <span className="st-tr-count">{activeCount}</span>}
          </button>
          <span className="st-who">
            <Icon name="user" size={14}/>
            <span className="st-who-name">{session.username}</span>
            {session.role === 'admin' && <span className="st-who-role">admin</span>}
          </span>
          <button type="button" className="iconbtn" onClick={signOut} aria-label="Sign out" title="Sign out">
            <Icon name="logout" size={16}/>
          </button>
        </div>
      </header>

      <div className={`st-body ${showTransfers || preview ? 'has-panel' : ''}`}
           style={{ '--st-rail-w': `${railW}px` }}>
        <aside className={`st-side ${showTree ? 'is-open' : ''}`}>
          {/* Only on the screens where the sidebar is a drawer: it needs a
              title and a way out that is not "guess to tap the dark part". */}
          <div className="st-side-head">
            <span className="label">Drives and folders</span>
            <button type="button" className="iconbtn" aria-label="Close" onClick={() => setShowTree(false)}>
              <Icon name="close" size={14}/>
            </button>
          </div>
          <Tree token={token} drives={drives} drivesLoading={drivesLoading && !drives.length} drive={drive} path={path} onNavigate={navigate}
                onOpenDrives={() => goView('dashboard')}
                trash={quota?.trash} trashActive={view === 'trash'}
                onOpenTrash={() => { goView('trash'); setShowTree(false); }}
                onDropTrash={dropOnTrash}
                refreshKey={refreshKey}
                dropTarget={null}
                onDropFiles={async (e, p, d) => {
                  e.preventDefault(); e.stopPropagation();
                  const rowsPayload = e.dataTransfer.getData('application/x-storage-rows');
                  if (rowsPayload) {
                    const moving = JSON.parse(rowsPayload);
                    // Same guard as the file list: a folder dropped on itself
                    // in the tree is the easiest way to hit this, and the
                    // server's only possible answer is a refusal.
                    if (isNoopMove(moving, p, d || drive)) return;
                    try {
                      const res = await api.batchMove(token, moving.files, moving.folders, p, d || drive);
                      const moved = (res.files || 0) + (res.folders || 0);
                      if (moved) {
                        toast('ok', 'Moved', `${res.files} file(s), ${res.folders} folder(s)`);
                        onChanged();
                      } else if (res.skipped?.length) {
                        toast('warn', 'Nothing moved', describeSkips(res.skipped));
                      }
                    } catch (ex) { toast('err', 'Move failed', ex.message); }
                    return;
                  }
                  const { entriesFromDrop } = await import('./transfers');
                  const entries = await entriesFromDrop(e.dataTransfer);
                  if (entries.length) checkUpload(entries, { drive: d || drive, path: p });
                }}/>
        </aside>

        <main className="st-main">
          {view === 'files' && (
            <Browser
              token={token} me={session} drives={drives} drive={drive} path={path}
              onNavigate={navigate} refreshKey={refreshKey} onChanged={onChanged} toast={toast}
              onUpload={(entries, target) => { checkUpload(entries, target); }}
              onDownload={(file) => { transfers.download(file); setShowTransfers(true); }}
              onPreview={openPreview} previewId={preview?.file?.id || null}
              selection={selection} onSelection={setSelection}
              query={query} onQuery={setQuery}
              onFileRows={adoptSiblings}
              clipboard={clipboard}
              onClipboard={setClipboard}
            />
          )}
          {view === 'home' && (
            <Home token={token} me={session} drives={drives} quota={quota} drivesLoading={drivesLoading}
                  transfers={transfers} trim={trim} toast={toast} onChanged={onChanged}
                  onOpenDrive={(d) => navigate(d, '/')}
                  onOpenFolder={(d, p) => navigate(d, p)}
                  onOpenFile={openFile}
                  onSearch={goSearch}
                  onUpload={(entries, target) => { checkUpload(entries, target); }}
                  onShowTransfers={() => setShowTransfers(true)}
                  goView={goView}/>
          )}
          {view === 'search' && (
            <AllFiles key={searchInit.n} token={token} drives={drives} me={session}
                      query={query} onQuery={setQuery}
                      initialSort={searchInit.sort} initialOwner={searchInit.owner}
                      onOpenFolder={(d, p) => navigate(d, p)}
                      onOpenFile={openFile} previewId={preview?.file?.id || null}
                      onDownload={(f) => { transfers.download(f); setShowTransfers(true); }}/>
          )}
          {view === 'dashboard' && (
            <Dashboard drives={drives} quota={quota} recent={recent} me={session} token={token}
                       loading={drivesLoading}
                       onOpenDrive={(d) => navigate(d, '/')}
                       onRefresh={() => { setDrivesLoading(true); loadDrives(); onChanged(); }}/>
          )}
          {view === 'trash' && (
            <Trash token={token} me={session} drives={drives} toast={toast} onChanged={onChanged}
                   refreshKey={refreshKey}
                   onOpenFolder={(d, p, fid) => navigate(d, p, fid ? [`f:${fid}`] : undefined)}/>
          )}
          {view === 'admin' && session.role === 'admin' && (
            <AdminUsers token={token} me={session} toast={toast}/>
          )}
        </main>

        {(preview || showTransfers) && (
          /* The divider. Only a grid column on wide screens — narrower than
             that the rail is a sheet over the page and there is nothing to
             drag it against, so CSS hides it. */
          <div className="st-drag" role="separator" aria-orientation="vertical" tabIndex={0}
               aria-label="Resize the panel" aria-valuenow={railW}
               aria-valuemin={RAIL_MIN} aria-valuemax={Math.round(railMax())}
               title="Drag to resize · double-click to reset"
               onPointerDown={dragRail} onKeyDown={nudgeRail}
               onDoubleClick={() => setRailW(RAIL_DEFAULT)}/>
        )}
        {(preview || showTransfers) && (
          <aside className="st-rail">
            {preview && (
              <Preview
                token={token} file={preview.file} me={session}
                canEdit={preview.file.owner_id === session?.id || session?.role === 'admin'}
                onClose={closePreview}
                startAt={startAt}
                onPosition={reportAt}
                onDownload={(f) => { transfers.download(f); setShowTransfers(true); }}
                onChanged={onChanged} toast={toast}
                onOpenTab={(at) => window.open(fileUrl(preview.file.id, at), '_blank', 'noopener')}
                trim={drives.find((d) => d.id === preview.file.drive)?.perms?.trim === false ? undefined : trim}
                directBase={directBaseOf(preview.file.drive)}
                onPrev={() => stepPreview(-1)} onNext={() => stepPreview(1)}
                hasPrev={preview.list.findIndex((f) => f.id === preview.file.id) > 0}
                hasNext={preview.list.findIndex((f) => f.id === preview.file.id) < preview.list.length - 1}
              />
            )}
            {showTransfers && (
              <TransferPanel items={transfers.items} active={transfers.active} limits={transfers.limits}
                             token={token} toast={toast} onChanged={onChanged}
                             onCancel={transfers.cancel} onRetry={transfers.retry}
                             onCancelAll={transfers.cancelAll} onClear={transfers.clearFinished}
                             onClose={() => setShowTransfers(false)} trim={trim}/>
            )}
          </aside>
        )}
      </div>

      {showTree && <button type="button" className="st-scrim" aria-label="Close folders" onClick={() => setShowTree(false)}/>}
      {(showTransfers || preview) && (
        <button type="button" className="st-scrim st-scrim--panel"
                aria-label={preview ? 'Close preview' : 'Close transfers'}
                onClick={() => { if (preview) closePreview(); else setShowTransfers(false); }}/>
      )}
      {driveRow && !driveRow.online && view === 'files' && (
        <div className="statusbar st-status">
          <span className="st-status-warn">{driveRow.label} is offline — showing nothing rather than a stale list.</span>
        </div>
      )}
      {dupDialog}
      {trim.dialog}
      {toastNode}
    </div>
  );
}
